import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

export type OpportunityStage =
  | 'PROSPECTING'
  | 'QUALIFICATION'
  | 'PROPOSAL'
  | 'NEGOTIATION'
  | 'CLOSING';

export interface Opportunity {
  id: string;
  tenantId: string;
  customerId: string;
  customerName: string | null;
  title: string;
  amount: number;
  currency: string;
  stage: OpportunityStage;
  outcome: 'WON' | 'LOST' | null;
  probability: number;
  ownerId: string;
  region: string | null;
  lossReason: string | null;
  quoteId: string | null;
  orderId: string | null;
  daysInStage: number;
  stageEnteredAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface FunnelStage {
  stage: OpportunityStage;
  label: string;
  count: number;
  value: number;
  conversionFromPrevious: number | null;
}

export interface StaleDeal {
  id: string;
  title: string;
  customerName: string | null;
  stage: OpportunityStage;
  value: number;
  daysIdle: number;
}

export interface FunnelMonth {
  month: string;
  won: number;
  forecast: number;
}

export interface Funnel {
  stages: FunnelStage[];
  overallConversion: number;
  averageCycleDays: number | null;
  weightedValue: number;
  staleDeals: StaleDeal[];
  monthly: FunnelMonth[];
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/* RF-023: UI do funil consome o agregado do backend (RF-007),
   nunca agrega status de Quote no cliente. */
export function useFunnel(days = 30): ReturnType<typeof useQuery<Funnel>> {
  return useQuery<Funnel>({
    queryKey: ['crm', 'funnel', days],
    queryFn: async () =>
      apiClient.get<Funnel>('/crm/funnel', { params: { days: String(days) } }),
  });
}

export function useOpportunities(params?: {
  stage?: string;
  customerId?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedResponse<Opportunity>>> {
  return useQuery<PaginatedResponse<Opportunity>>({
    queryKey: ['crm', 'opportunities', params],
    queryFn: async () =>
      apiClient.get<PaginatedResponse<Opportunity>>('/opportunities', {
        params: {
          ...(params?.stage ? { stage: params.stage } : {}),
          ...(params?.customerId ? { customerId: params.customerId } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 100),
        },
      }),
  });
}

export interface OpportunityInput {
  customerId: string;
  title: string;
  amount: number;
  ownerId: string;
  stage?: OpportunityStage;
  probability?: number;
  region?: string;
  expectedCloseDate?: string;
  description?: string;
}

function useInvalidateOpportunities() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['crm', 'opportunities'] });
    queryClient.invalidateQueries({ queryKey: ['crm', 'funnel'] });
  };
}

export function useCreateOpportunity() {
  const invalidate = useInvalidateOpportunities();
  return useMutation<Opportunity, unknown, OpportunityInput, unknown>({
    mutationFn: (input: OpportunityInput) =>
      apiClient.post<Opportunity>('/opportunities', input),
    onSuccess: () => invalidate(),
  });
}

export function useMoveOpportunityStage() {
  const invalidate = useInvalidateOpportunities();
  return useMutation<Opportunity, unknown, { id: string; stage: OpportunityStage }, unknown>({
    mutationFn: ({ id, stage }) =>
      apiClient.patch<Opportunity>(`/opportunities/${id}/stage`, { stage }),
    onSuccess: () => invalidate(),
  });
}

export function useWinOpportunity() {
  const invalidate = useInvalidateOpportunities();
  return useMutation<Opportunity, unknown, { id: string; orderId?: string }, unknown>({
    mutationFn: ({ id, orderId }) =>
      apiClient.post<Opportunity>(`/opportunities/${id}/win`, { orderId }),
    onSuccess: () => invalidate(),
  });
}

export function useLoseOpportunity() {
  const invalidate = useInvalidateOpportunities();
  return useMutation<Opportunity, unknown, { id: string; reason: string }, unknown>({
    mutationFn: ({ id, reason }) =>
      apiClient.post<Opportunity>(`/opportunities/${id}/lose`, { reason }),
    onSuccess: () => invalidate(),
  });
}
